import { type ReactElement, type ReactNode, useEffect, useState, ChangeEvent } from "react"
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "src/app/store";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import Modal from "src/components/Modal";
import { UserType } from "../../types/UserType";
import Button from "src/components/Button";
import { PasswordProfileType } from "../../types/PasswordProfileType";
import { UserDealerAccessType } from "../../types/UserDealerAccessType";
import { DealerType } from "src/features/admin/types/DealerType";
import { Address } from "src/features/admin/types/Address";
import PhoneNumberInput from "src/components/PhoneNumberInput";
import { createUser, updateUser } from "../../hooks/addUpdateUserResponsibility";
import Saving from "src/components/Saving";

export interface UserModalProps {
        title: string,
        isOpen: boolean,
        selectedUser: UserType | undefined,
        onClose: () => void,
        passwordProfiles: Array<PasswordProfileType>,
        children?: ReactNode
}

export default function UserModal(props: UserModalProps): ReactElement {
        const { register, control, handleSubmit, reset, setError, formState: { errors, isSubmitting } } = useForm<UserType>({
                defaultValues: props.selectedUser
        });
        const [tabSelected, setTabSelect] = useState<string>("userDetails");
        const [userAccessList, setUserAccessList] = useState<UserDealerAccessType[]>([]);
        const [localDealers, setDealers] = useState<DealerType[]>();
        const dispatch = useDispatch<AppDispatch>();

        let { dealers } = useSelector((state: RootState) => state.dealers);
        let { dealer } = useSelector((state: RootState) => state.dealer);
        let { authUser } = useSelector((state: RootState) => state.auth);
        const { users, usersLoading, usersError } = useSelector((state: RootState) => state.users);
        const { user, userLoading, userError } = useSelector((state: RootState) => state.user);

        const onSubmit: SubmitHandler<UserType> = async (data) => {
                if (data.DealerId == 1) {
                        data.UserType = "admin";
                        data.UserAccesses = userAccessList;
                }
                else {
                        data.UserType = "dealer";
                        data.UserAccesses = userAccessList;
                }
                if (props.title === "Add User") {
                        await dispatch(createUser(data));
                }
                else if (props.title === "Edit User") {
                        await dispatch(updateUser(data));
                }
        }

        const tabClick = (event: React.MouseEvent<HTMLDivElement>) => {
                setTabSelect(event.currentTarget.id);
        }

        const dealerSelected = (event: ChangeEvent<HTMLInputElement>) => {
                let userAccList = [...userAccessList];
                let dealerId = parseInt(event.currentTarget.id);
                let hasAccessTo = event.target.checked;
                let name = event.target.name;
                let userAccObj = { Id: dealerId, Name: name, HasAccessTo: hasAccessTo }
                let indexOfUserAccElem = userAccList.findIndex(elem => elem.Id === dealerId);
                userAccList[indexOfUserAccElem] = userAccObj;
                setUserAccessList(userAccList);
        }

        useEffect(() => {
                let error = "";
                if (userError != null) {
                        error = userError;
                }
                else {
                        //making sure the below array is initialized to satisfy typescript
                        let dealerAccessListFromDB: Array<UserDealerAccessType> | undefined = props.selectedUser?.UserAccesses ?? [];
                        setUserAccessList(dealerAccessListFromDB);
                        reset(props.selectedUser);
                        if (authUser?.UserType == "admin") {
                                let ldealers: DealerType[] = [];
                                if (dealers.length > 0) {
                                        //creating a new array rather than just referencing
                                        //as states are immutable hence the below operation
                                        ldealers = [...dealers];
                                }
                                // let dealer0: DealerType | undefined = ldealers.find(a => a.Id === 1);
                                // if (dealer0 !== undefined) {
                                //         ldealers.push(dealer0);
                                // }
                                setDealers(ldealers);
                        }
                        else {
                                let ldealers = [];
                                ldealers.push(dealer);
                                setDealers(ldealers);
                        }
                }
                if (user !== null) {
                        props.onClose();
                }
                setError("root", {
                        type: "manual",
                        message: error,
                });
        }, [user, userError])

        return <>
                <Modal isOpen={props.isOpen} modalSize="custom" height="52" onClose={props.onClose}>
                        <h2 className="text-lg font-bold">
                                {props.title}
                        </h2>
                        <form onSubmit={handleSubmit(onSubmit)}>
                                <div className="flex justify-around cursor-pointer">
                                        <div className={tabSelected === "userDetails" ? "flex hover:bg-blue-300 w-full border-b border-solid border-blue-500 bg-blue-300" : "flex hover:bg-blue-300 w-full"} onClick={tabClick} id="userDetails">User Details</div>
                                        <div className={tabSelected === "dealerAccess" ? "flex hover:bg-blue-300 w-full border-b border-solid border-blue-500 bg-blue-300" : "flex hover:bg-blue-300 w-full"} onClick={tabClick} id="dealerAccess">Edit Dealer Access</div>
                                </div>
                                {
                                        tabSelected === "userDetails" ?
                                                <div className="min-h-[47rem] max-h-[47rem]">
                                                        <div className="mb-4">
                                                                <label className="block text-sm">Dealer Id</label>
                                                                <select className="w-full px-3 py-2 border rounded-lg" {...register("DealerId", { required: "DealerId is required" })}>
                                                                        {
                                                                                localDealers?.map((locdealer) => (
                                                                                        <option key={locdealer.Id} value={locdealer.Id}>{locdealer.Name}</option>
                                                                                ))}
                                                                </select>
                                                                {errors.DealerId && (
                                                                        <div className="text-red-500">{errors.DealerId.message}</div>
                                                                )}
                                                        </div>
                                                        <div className="mb-4">
                                                                <label className="block text-sm">First Name</label>
                                                                <input className="w-full px-3 py-2 border rounded-lg" placeholder="Enter FirstName" {...register("FirstName", { required: "First Name is required", maxLength: { value: 35, message: "Enter a FirstName" } })} />
                                                                {errors.FirstName && (
                                                                        <div className="text-red-500">{errors.FirstName.message}</div>
                                                                )}
                                                        </div>
                                                        <div className="mb-4">
                                                                <label className="block text-sm">Last Name</label>
                                                                <input className="w-full px-3 py-2 border rounded-lg" placeholder="Enter LastName" {...register("LastName", { required: "Last Name ius required", maxLength: { value: 35, message: "Enter a LastName" } })} />
                                                                {errors.LastName && (
                                                                        <div className="text-red-500">{errors.LastName.message}</div>
                                                                )}
                                                        </div>
                                                        <div className="mb-4">
                                                                <label className="block text-sm">Email</label>
                                                                <input className="w-full px-3 py-2 border rounded-lg" placeholder="Enter email" {...register("EmailId", { required: "Email is required", pattern: { value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/, message: "Enter a valid Email Address" }, })} />
                                                                {errors.EmailId && (
                                                                        <div className="text-red-500">{errors.EmailId.message}</div>
                                                                )}
                                                        </div>
                                                        <div className="mb-4">
                                                                <label className="block text-sm">Password</label>
                                                                <input type="password" className="w-full text-sm text-gray-800 bg-gray-100 focus:bg-transparent pl-4 pr-10 py-3.5 rounded-xs outline-blue-600" placeholder="Enter password" {...register("Password", { required: "Password is required", minLength: { value: 8, message: "Password must be atleast 8 characters" }, })} />
                                                                {errors.Password && (
                                                                        <div className="text-red-500">{errors.Password.message}</div>
                                                                )}
                                                        </div>
                                                        <div className="mb-4">
                                                                <label className="block text-sm">Phone Number</label>
                                                                <Controller name="PhoneNumber" control={control} defaultValue="" render={({ field }) => (
                                                                        <PhoneNumberInput value={field.value} onValueChange={field.onChange} />
                                                                )}
                                                                />
                                                                {errors.PhoneNumber && (
                                                                        <div className="text-red-500">{errors.PhoneNumber.message}</div>
                                                                )}
                                                        </div>
                                                        <div className="mb-4">
                                                                <label className="block text-sm">Notes</label>
                                                                <input className="w-full px-3 py-2 border rounded-lg" {...register("Notes", { maxLength: { value: 120, message: "Length Cannot be more than 120" } })} />
                                                                {errors.Notes && (
                                                                        <div className="text-red-500">{errors.Notes.message}</div>
                                                                )}
                                                        </div>
                                                        <div className="mb-4 flex items-center justify-evenly">
                                                                <div>
                                                                        <label className="text-sm">Registered</label>
                                                                </div>
                                                                <div>
                                                                        <input type="checkbox" className="w-full px-3 py-2 border rounded-lg" {...register("Registered")} disabled={true} />
                                                                </div>
                                                        </div>
                                                        <div className="mb-4 flex items-center justify-evenly">
                                                                <div>
                                                                        <label className="text-sm">Enabled</label>
                                                                </div>
                                                                <div>
                                                                        <input type="checkbox" className="w-full px-3 py-2 border rounded-lg" {...register("Enabled")} />
                                                                </div>
                                                        </div>
                                                        <div className="mb-4 flex items-center justify-evenly">
                                                                <div>
                                                                        <label className="text-sm">Primary User</label>
                                                                </div>
                                                                <div>
                                                                        <input type="checkbox" className="w-full px-3 py-2 border rounded-lg" {...register("PrimaryUser")} />
                                                                </div>
                                                        </div>
                                                        <div className="mb-4">
                                                                <label className="block text-sm">Profile ID</label>
                                                                <select className="w-full px-3 py-2 border rounded-lg" {...register("PasswordProfileId")}>
                                                                        {props.passwordProfiles.map((passwordProfile) => (
                                                                                <option key={passwordProfile.Id} value={passwordProfile.Id}>{passwordProfile.Name}</option>
                                                                        ))}
                                                                </select>
                                                        </div>
                                                </div> :
                                                <div className="min-h-[47rem] max-h-[47rem] overflow-y-auto">
                                                        {
                                                                userAccessList?.map((userAccess) => {
                                                                        let dealerId = userAccess?.Id;
                                                                        let dealerName = userAccess?.Name;
                                                                        let userAccessHas = userAccess.HasAccessTo;
                                                                        return <div className="mb-4 flex items-center justify-evenly">
                                                                                <div>
                                                                                        <label className="text-sm">{dealerName}</label>
                                                                                </div>
                                                                                <div>
                                                                                        <input id={dealerId?.toString()} name={dealerName} type="checkbox" className="w-full px-3 py-2 border rounded-lg" checked={userAccessHas} onChange={dealerSelected} />
                                                                                </div>
                                                                        </div>

                                                                }
                                                                )
                                                        }
                                                </div>
                                }
                                <div className="flex gap-16 justify-around">
                                        <Button className="" size="lg" type="reset" buttonStyle="delete">Reset</Button>
                                        <Button className="" size="lg" type="submit" buttonStyle="primary">{isSubmitting ? <Saving title="Saving" color="black" /> : "Save"}</Button>
                                </div>
                        </form>
                        {errors.root && (
                                <div className="text-red-500 text-center">{errors.root.message}</div>
                        )}
                </Modal>
        </>
}
